import { Children, cloneElement, isValidElement, useEffect, useId, useRef, useState } from "react";
import { FaBars, FaTimes } from "react-icons/fa";
import logo from "../../Assets/EMEI logo.jpeg";
import { useLocation } from "react-router-dom";
import "./SiteHeader.css";

export default function SiteHeader({ children, overlay = false, animateEntrance = true }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const toggleRef = useRef(null);
  const headerRef = useRef(null);
  const progressRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    let frame = null;
    function updateProgress() {
      frame = null;
      const root = document.documentElement;
      const scrollableHeight = root.scrollHeight - root.clientHeight;
      const progress = scrollableHeight > 0 ? Math.min(1, Math.max(0, window.scrollY / scrollableHeight)) : 0;
      if (progressRef.current) progressRef.current.style.transform = `scaleX(${progress})`;
    }
    function scheduleUpdate() {
      if (frame === null) frame = requestAnimationFrame(updateProgress);
    }
    scheduleUpdate();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);
    const observer = new ResizeObserver(scheduleUpdate);
    observer.observe(document.body);
    observer.observe(document.documentElement);
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [pathname]);
  useEffect(() => { setMenuOpen(false); }, [pathname]);
  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1025px)");
    const closeOnDesktop = () => { if (desktop.matches) setMenuOpen(false); };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);
  useEffect(() => {
    if (!menuOpen) return;
    function closeOutside(event) {
      if (!headerRef.current?.contains(event.target)) setMenuOpen(false);
    }
    document.addEventListener("pointerdown", closeOutside);
    function closeOnEscape(event) {
      if (event.key === "Escape") {
        setMenuOpen(false);
        toggleRef.current?.focus();
      }
    }
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOutside);
    };
  }, [menuOpen]);
  return (
    <>
      <header ref={headerRef} className={`site-header${animateEntrance ? " site-header--arriving" : ""}`}>
        <nav className="site-header__nav" aria-label="Main navigation">
          <img className="site-header__logo" src={logo} alt="EMEI logo" />
          <button
            ref={toggleRef}
            type="button"
            className="site-header__menu-toggle"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
          </button>
          <ul id={menuId} className={`site-header__links${menuOpen ? " site-header__links--open" : ""}`} onClick={(event) => {
            if (event.target.closest("a")) setMenuOpen(false);
          }}>
            {animateEntrance ? Children.map(children, (child, index) =>
              isValidElement(child) ? cloneElement(child, {
                "data-aos": undefined,
                "data-aos-duration": undefined,
                "data-aos-delay": undefined,
                "data-aos-once": undefined,
                style: { ...child.props.style, "--header-arrival-delay": `${140 + index * 100}ms` },
              }) : child
            ) : children}
          </ul>
        </nav>
        <div className="site-header__scroll-track" aria-hidden="true">
          <div className="site-header__scroll-progress" ref={progressRef} />
        </div>
      </header>
      {!overlay && <div className="site-header__spacer" aria-hidden="true" />}
    </>
  );
}
